"use client";

import { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import Api from "../../api/Api";

export default function CampaignDetailsModal({
  modal_center,
  setmodal_center,
  tog_center,
  campaign,
  onStatusUpdate,
}) {
  const [isUpdating, setIsUpdating] = useState(false);

  const updateCampaignStatus = async (newStatus) => {
    if (!campaign || !campaign.id) return;

    setIsUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await Api.put(
        `/admin/campaigns/${campaign.id}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(`Campaign status updated to ${newStatus}`, {
          position: "bottom-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          theme: "colored",
        });

        // Update the campaign in parent component
        if (onStatusUpdate) {
          onStatusUpdate(campaign.id, newStatus);
        }

        // Close modal
        tog_center();
      } else {
        toast.error(
          response.data.message || "Failed to update campaign status",
          {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
          }
        );
      }
    } catch (error) {
      console.error("Error updating campaign status:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const approveCampaign = async () => {
    if (!campaign || !campaign.id) return;

    setIsUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await Api.post(
        `/admin/campaigns/${campaign.id}/approve`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Campaign approved successfully", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });

        // Update the campaign in parent component
        if (onStatusUpdate) {
          onStatusUpdate(campaign.id, "active");
        }

        // Close modal
        tog_center();
      } else {
        toast.error(response.data.message || "Failed to approve campaign", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error approving campaign:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const rejectCampaign = async () => {
    if (!campaign || !campaign.id) return;

    if (
      !window.confirm(
        "Are you sure you want to reject this campaign? This action cannot be undone."
      )
    ) {
      return;
    }

    setIsUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await Api.post(
        `/admin/campaigns/${campaign.id}/reject`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Campaign rejected successfully", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });

        // Update the campaign in parent component
        if (onStatusUpdate) {
          onStatusUpdate(campaign.id, "cancelled");
        }

        // Close modal
        tog_center();
      } else {
        toast.error(response.data.message || "Failed to reject campaign", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error rejecting campaign:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: "secondary", text: "Draft" },
      need_approve: { color: "warning", text: "Needs Approval" },
      active: { color: "success", text: "Active" },
      inactive: { color: "secondary", text: "Inactive" },
      suspended: { color: "warning", text: "Suspended" },
      paused: { color: "info", text: "Paused" },
      completed: { color: "primary", text: "Completed" },
      cancelled: { color: "danger", text: "Cancelled" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      text: "Unknown",
    };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!campaign) return null;

  const isNeedingApproval = campaign.status === "need_approve";

  return (
    <Modal
      isOpen={modal_center}
      toggle={tog_center}
      centered
      size="lg"
      className="campaign-details-modal"
    >
      <ModalHeader toggle={tog_center}>
        <div className="d-flex align-items-center">
          <i className="ri-megaphone-line me-2 text-primary"></i>
          Campaign Details
          {isNeedingApproval && (
            <Badge color="warning" className="ms-2">
              <i className="ri-time-line me-1"></i>
              Needs Approval
            </Badge>
          )}
        </div>
      </ModalHeader>

      <ModalBody>
        <div className="campaign-details">
          {/* Campaign Header */}
          <Row className="mb-4">
            <Col md={8}>
              <div className="d-flex align-items-start">
                {campaign.image_url && (
                  <img
                    src={
                      "https://api.tafaseelapp.com/storage/" +
                        campaign.image_url || "/placeholder.svg"
                    }
                    alt={campaign.title}
                    className="rounded me-3"
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                    }}
                  />
                )}
                <div>
                  <h5 className="mb-2">{campaign.title}</h5>
                  <p className="text-muted mb-2">{campaign.description}</p>
                  <div className="d-flex gap-2 align-items-center">
                    {getStatusBadge(campaign.status)}
                    {campaign.category && (
                      <Badge color="light" className="text-dark">
                        {campaign.category.name}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-end">
              <div className="campaign-price">
                <h4 className="text-success mb-0">
                  {formatPrice(campaign.price)}
                </h4>
                <small className="text-muted">Campaign Budget</small>
              </div>
            </Col>
          </Row>

          {/* Campaign Details */}
          <Row className="mb-4">
            <Col md={6}>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-information-line me-2"></i>
                  Campaign Information
                </h6>
                <div className="info-item mb-2">
                  <strong>Campaign ID:</strong> #{campaign.id}
                </div>
                <div className="info-item mb-2">
                  <strong>Location:</strong> {campaign.location}
                </div>
                <div className="info-item mb-2">
                  <strong>Campaign Date:</strong>{" "}
                  {formatDate(campaign.campaign_date)}
                </div>
                <div className="info-item mb-2">
                  <strong>Views:</strong> {campaign.views || 0}
                </div>
                <div className="info-item mb-2">
                  <strong>Created:</strong>{" "}
                  {formatDateTime(campaign.created_at)}
                </div>
                <div className="info-item mb-2">
                  <strong>Last Updated:</strong>{" "}
                  {formatDateTime(campaign.updated_at)}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-building-line me-2"></i>
                  Business Information
                </h6>
                <div className="info-item mb-2">
                  <strong>Business Name:</strong> {campaign.user.name}
                </div>
                {campaign.user.company_name && (
                  <div className="info-item mb-2">
                    <strong>Company:</strong> {campaign.user.company_name}
                  </div>
                )}
                <div className="info-item mb-2">
                  <strong>Email:</strong> {campaign.user.email}
                </div>
                {campaign.user.phone && (
                  <div className="info-item mb-2">
                    <strong>Phone:</strong> {campaign.user.phone}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Campaign Rules */}
          {campaign.rules && (
            <Row className="mb-4">
              <Col>
                <div className="info-section">
                  <h6 className="text-primary mb-3">
                    <i className="ri-file-list-line me-2"></i>
                    Campaign Rules & Requirements
                  </h6>
                  <div className="rules-content p-3 bg-light rounded">
                    <p className="mb-0">{campaign.rules}</p>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Applications Summary */}
          <Row className="mb-4">
            <Col>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-user-star-line me-2"></i>
                  Applications Summary
                </h6>
                <Row>
                  <Col md={3}>
                    <div className="stat-card text-center p-3 border rounded">
                      <h4 className="mb-1">{campaign.applications_count}</h4>
                      <small className="text-muted">Total Applications</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="stat-card text-center p-3 border rounded">
                      <h4 className="mb-1 text-warning">
                        {campaign.pending_applications}
                      </h4>
                      <small className="text-muted">Pending</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="stat-card text-center p-3 border rounded">
                      <h4 className="mb-1 text-success">
                        {campaign.approved_applications}
                      </h4>
                      <small className="text-muted">Approved</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="stat-card text-center p-3 border rounded">
                      <h4 className="mb-1 text-danger">
                        {campaign.rejected_applications}
                      </h4>
                      <small className="text-muted">Rejected</small>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          {/* Applications List */}
          {campaign.applications && campaign.applications.length > 0 && (
            <Row>
              <Col>
                <div className="info-section">
                  <h6 className="text-primary mb-3">
                    <i className="ri-team-line me-2"></i>
                    Recent Applications
                  </h6>
                  <div className="applications-list">
                    {campaign.applications.slice(0, 5).map((application) => (
                      <div
                        key={application.id}
                        className="application-item d-flex justify-content-between align-items-center p-2 border-bottom"
                      >
                        <div>
                          <strong>{application.influencer.name}</strong>
                          <br />
                          <small className="text-muted">
                            {application.influencer.email}
                          </small>
                          {application.message && (
                            <div>
                              <small className="text-muted">
                                "{application.message}"
                              </small>
                            </div>
                          )}
                        </div>
                        <div className="text-end">
                          <Badge
                            color={
                              application.status === "approved"
                                ? "success"
                                : application.status === "rejected"
                                ? "danger"
                                : "warning"
                            }
                          >
                            {application.status}
                          </Badge>
                          <br />
                          <small className="text-muted">
                            {formatDateTime(application.created_at)}
                          </small>
                        </div>
                      </div>
                    ))}
                    {campaign.applications.length > 5 && (
                      <div className="text-center p-2">
                        <small className="text-muted">
                          And {campaign.applications.length - 5} more
                          applications...
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <div className="d-flex justify-content-between w-100">
          {/* Approval Actions for campaigns needing approval */}
          {isNeedingApproval ? (
            <div className="approval-actions">
              <h6 className="mb-2 text-warning">
                <i className="ri-time-line me-2"></i>
                Campaign Approval Required:
              </h6>
              <div className="d-flex gap-2">
                <Button
                  color="success"
                  onClick={approveCampaign}
                  disabled={isUpdating}
                >
                  <i className="ri-check-line me-1"></i>
                  {isUpdating ? "Approving..." : "Approve Campaign"}
                </Button>
                <Button
                  color="danger"
                  onClick={rejectCampaign}
                  disabled={isUpdating}
                >
                  <i className="ri-close-line me-1"></i>
                  {isUpdating ? "Rejecting..." : "Reject Campaign"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="status-actions">
              <h6 className="mb-2">Update Status:</h6>
              <div className="d-flex gap-2 flex-wrap">
                {campaign.status !== "active" && (
                  <Button
                    color="success"
                    size="sm"
                    onClick={() => updateCampaignStatus("active")}
                    disabled={isUpdating}
                  >
                    <i className="ri-play-line me-1"></i>
                    Make Active
                  </Button>
                )}
                {campaign.status !== "suspended" && (
                  <Button
                    color="warning"
                    size="sm"
                    onClick={() => updateCampaignStatus("suspended")}
                    disabled={isUpdating}
                  >
                    <i className="ri-pause-line me-1"></i>
                    Suspend
                  </Button>
                )}
                {campaign.status !== "completed" && (
                  <Button
                    color="info"
                    size="sm"
                    onClick={() => updateCampaignStatus("completed")}
                    disabled={isUpdating}
                  >
                    <i className="ri-check-line me-1"></i>
                    Mark Complete
                  </Button>
                )}
                {campaign.status !== "cancelled" && (
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => updateCampaignStatus("cancelled")}
                    disabled={isUpdating}
                  >
                    <i className="ri-close-line me-1"></i>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          )}
          <div>
            <Button color="secondary" onClick={tog_center}>
              Close
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
}
